import type { Address, Client, CreateAddressRequest } from '@frontend/api-types';
import { match } from '@frontend/duck-tape';
import { useCallback, useEffect, useState } from '@frontend/react';
import { CardContainer, YStack } from '@frontend/web-react';
import { useCreateAddressMutation, useEditProfileMutation, useUpdateAddressMutation } from '@frontend/web/hooks';
import { handleMutation } from '@frontend/web/utils';
import { useReducer } from 'react';

import { PROFILE_WIZARD_STEP_ORDER } from './ProfileWizardCard.constants';
import type { ProfileWizardAction, ProfileWizardState, ProfileWizardStep } from './ProfileWizardCard.types';
import { PROFILE_WIZARD_STEP } from './ProfileWizardCard.types';
import { ProfileWizardStepIndicator } from './ProfileWizardStepIndicator';
import { AddressStep } from './steps/AddressStep';
import { BirthdayStep } from './steps/BirthdayStep';
import { PhoneNumberStep } from './steps/PhoneNumberStep';

type ProfileWizardContentProps = {
  client: Client;
  existingAddress: MaybeUndefined<Address>;
  initialState: ProfileWizardState;
  onAllComplete?: () => void;
};

const getNextIncompleteStep = (completedSteps: ReadonlyArray<ProfileWizardStep>): ProfileWizardStep | undefined =>
  PROFILE_WIZARD_STEP_ORDER.find((step) => !completedSteps.includes(step));

const profileWizardReducer = (state: ProfileWizardState, action: ProfileWizardAction): ProfileWizardState =>
  match(action)
    .with({ type: 'completeCurrentStep' }, () => {
      const newCompleted = state.completedSteps.includes(state.currentStep)
        ? state.completedSteps
        : [...state.completedSteps, state.currentStep];
      return {
        completedSteps: newCompleted,
        currentStep: getNextIncompleteStep(newCompleted) ?? state.currentStep,
      };
    })
    .with({ type: 'goToStep' }, ({ step }) => (step === state.currentStep ? state : { ...state, currentStep: step }))
    .with({ type: 'syncApiSteps' }, ({ completedSteps: apiSteps }) => ({
      ...state,
      completedSteps: PROFILE_WIZARD_STEP_ORDER.filter(
        (step) => state.completedSteps.includes(step) || apiSteps.includes(step),
      ),
    }))
    .exhaustive();

export const ProfileWizardContent = ({
  client,
  existingAddress,
  initialState,
  onAllComplete,
}: ProfileWizardContentProps) => {
  const [state, dispatch] = useReducer(profileWizardReducer, initialState);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editProfile] = useEditProfileMutation();
  const [createAddress] = useCreateAddressMutation();
  const [updateAddress] = useUpdateAddressMutation();

  const isAllComplete = state.completedSteps.length === PROFILE_WIZARD_STEP_ORDER.length;

  useEffect(() => {
    dispatch({ completedSteps: initialState.completedSteps, type: 'syncApiSteps' });
  }, [initialState.completedSteps]);

  useEffect(() => {
    if (isAllComplete) {
      onAllComplete?.();
    }
  }, [isAllComplete, onAllComplete]);

  const completeCurrentStep = useCallback(() => {
    dispatch({ type: 'completeCurrentStep' });
  }, []);

  const handlePhoneSubmit = useCallback(
    async (phoneNumber: string) => {
      setIsSubmitting(true);
      await handleMutation({
        mutation: () => editProfile({ phoneNumber }).unwrap(),
        onComplete: () => setIsSubmitting(false),
        onSuccess: completeCurrentStep,
        showSuccessToast: false,
      });
    },
    [completeCurrentStep, editProfile],
  );

  const handleBirthdaySubmit = useCallback(
    async (birthday: DateISOString) => {
      setIsSubmitting(true);
      await handleMutation({
        mutation: () => editProfile({ birthday }).unwrap(),
        onComplete: () => setIsSubmitting(false),
        onSuccess: completeCurrentStep,
        showSuccessToast: false,
      });
    },
    [completeCurrentStep, editProfile],
  );

  const handleAddressSubmit = useCallback(
    async (address: CreateAddressRequest) => {
      setIsSubmitting(true);
      const mutation = existingAddress ? () => updateAddress(address).unwrap() : () => createAddress(address).unwrap();
      await handleMutation({
        mutation,
        onComplete: () => setIsSubmitting(false),
        onSuccess: completeCurrentStep,
        showSuccessToast: false,
      });
    },
    [completeCurrentStep, createAddress, existingAddress, updateAddress],
  );

  const handleGoToStep = useCallback((step: ProfileWizardStep) => {
    dispatch({ step, type: 'goToStep' });
  }, []);

  return (
    <CardContainer className="overflow-visible p-lg border-borderTertiary">
      <YStack gapY="lg">
        <ProfileWizardStepIndicator
          completedSteps={state.completedSteps}
          currentStep={state.currentStep}
          onGoToStep={handleGoToStep}
        />
        {match(state.currentStep)
          .with(PROFILE_WIZARD_STEP.address, () => (
            <AddressStep existingAddress={existingAddress} isSubmitting={isSubmitting} onSubmit={handleAddressSubmit} />
          ))
          .with(PROFILE_WIZARD_STEP.birthday, () => (
            <BirthdayStep initialValue={client.birthday} isSubmitting={isSubmitting} onSubmit={handleBirthdaySubmit} />
          ))
          .with(PROFILE_WIZARD_STEP.phoneNumber, () => (
            <PhoneNumberStep
              initialValue={client.phoneNumber}
              isSubmitting={isSubmitting}
              onSubmit={handlePhoneSubmit}
            />
          ))
          .exhaustive()}
      </YStack>
    </CardContainer>
  );
};
